/**
 * 도메인 이벤트 helper (Phase 2 — 온톨로지 V0).
 *
 * 모든 이벤트는 `domain.action` 형식의 label 로 통일.
 *   예: "analyze.viewed", "trade.executed", "recommend.shown"
 *
 * 이 파일에 등록된 라벨만 화이트리스트 통과 (analytics-whitelist.ts 의 strict mode).
 *
 * Phase 2 범위:
 *   • analyze   — 기업 분석 페이지 진입 / 리포트 생성 / 모듈 클릭
 *   • compare   — 비교 분석
 *   • watchlist — 관심종목 추가/제거
 *   • chat      — AI 채팅
 *   • ontology  — 기업 관계 그래프 클릭
 *   • trade     — 매매 실행 (수동 / KIS / 키움 / CSV) + outcome (1주/1달/1년)
 *   • community — 게시글 / 댓글 / 좋아요 / 북마크 / 리더보드
 *   • payment   — 결제 (trial / paid / downgrade / cancel)
 *   • subscribe — 가격 페이지 (tier 조회 / 플랜 클릭)
 *   • recommend — 추천 노출 / 클릭 / dismiss (V1 추천 정확도 baseline)
 *
 * 이벤트는 비동기 enqueue — 호출자는 결과 신경 쓸 필요 X.
 */

import { trackCustom } from "@/lib/analytics";

// ─────────────────────────────────────────────────────────────
// 공통 타입
// ─────────────────────────────────────────────────────────────

export type TradeSide = "buy" | "sell";
export type TradeSource = "manual" | "kis" | "kiwoom" | "csv";
export type RecommendSurface =
  | "analyze.related"        // 분석 페이지 하단 "이 기업과 관련된 기업"
  | "analyze.preBuy"          // 매매 직전 "이 기업 사기 전 봐야 할 기업"
  | "home.daily"              // 메인 "당신을 위한 오늘의 추천"
  | "tax.season"              // 양도세 시즌
  | "insider.alert"           // 인사이더 alert
  | "push.notification";      // 푸시
export type RecommendAlgorithm = "embedding" | "collab" | "graph" | "fusion";

// ─────────────────────────────────────────────────────────────
// ANALYZE — 기업 분석
// ─────────────────────────────────────────────────────────────
export const ANALYZE = {
  viewed: (ticker: string) => trackCustom("analyze.viewed", { ticker }),
  jobStarted: (ticker: string, jobId: string) =>
    trackCustom("analyze.jobStarted", { ticker, jobId }),
  reportRendered: (ticker: string, jobId: string, renderMs: number) =>
    trackCustom("analyze.reportRendered", { ticker, jobId, renderMs }),
  sectionExpanded: (ticker: string, section: string) =>
    trackCustom("analyze.sectionExpanded", { ticker, section }),
};

// ─────────────────────────────────────────────────────────────
// COMPARE — 비교 분석
// ─────────────────────────────────────────────────────────────
export const COMPARE = {
  viewed: (tickers: string[]) =>
    trackCustom("compare.viewed", { tickers, count: tickers.length }),
  tickerAdded: (ticker: string) => trackCustom("compare.tickerAdded", { ticker }),
  tickerRemoved: (ticker: string) =>
    trackCustom("compare.tickerRemoved", { ticker }),
};

// ─────────────────────────────────────────────────────────────
// WATCHLIST — 관심종목
// ─────────────────────────────────────────────────────────────
export const WATCHLIST = {
  added: (ticker: string) => trackCustom("watchlist.added", { ticker }),
  removed: (ticker: string) => trackCustom("watchlist.removed", { ticker }),
  viewed: (count: number) => trackCustom("watchlist.viewed", { count }),
};

// ─────────────────────────────────────────────────────────────
// CHAT — AI 채팅
// ─────────────────────────────────────────────────────────────
export const CHAT = {
  messageSent: (params: {
    ticker?: string | null;
    surface: "dashboard" | "chart" | "report";
    messageLength: number;
  }) => trackCustom("chat.messageSent", params),
  responseRendered: (params: {
    ticker?: string | null;
    surface: "dashboard" | "chart" | "report";
    responseLength: number;
    durationMs: number;
  }) => trackCustom("chat.responseRendered", params),
};

// ─────────────────────────────────────────────────────────────
// ONTOLOGY — 기업 관계 그래프
// ─────────────────────────────────────────────────────────────
export const ONTOLOGY = {
  graphViewed: (ticker: string) => trackCustom("ontology.graphViewed", { ticker }),
  relatedClicked: (params: {
    fromTicker: string;
    toTicker: string;
    relationType: "competitor" | "supplier" | "customer" | "subsidiary" | "sectorPeer";
  }) => trackCustom("ontology.relatedClicked", params),
};

// ─────────────────────────────────────────────────────────────
// TRADE — 매매 실행 + 결과 (KIS / 키움 / CSV / 수동)
// ─────────────────────────────────────────────────────────────
export const TRADE = {
  /** 매매 실행 — 한 건의 매수/매도 */
  executed: (params: {
    ticker: string;
    side: TradeSide;
    qty: number;
    price: number;
    source: TradeSource;
    executedAt: number; // epoch ms
    brokerTradeId?: string;
    fromAnalysisJobId?: string;  // 분석 후 매매면 jobId 연결
    fromWatchlist?: boolean;
  }) => trackCustom("trade.executed", params),

  /** 매매 후 1주 결과 (cron 으로 발사) */
  outcome1w: (params: {
    ticker: string;
    side: TradeSide;
    executedAt: number;
    returnPct: number;
  }) => trackCustom("trade.outcome1w", params),

  outcome1m: (params: {
    ticker: string;
    side: TradeSide;
    executedAt: number;
    returnPct: number;
  }) => trackCustom("trade.outcome1m", params),

  outcome1y: (params: {
    ticker: string;
    side: TradeSide;
    executedAt: number;
    returnPct: number;
  }) => trackCustom("trade.outcome1y", params),

  csvImported: (count: number) => trackCustom("trade.csvImported", { count }),
  kisConnected: () => trackCustom("trade.kisConnected", {}),
  kisSynced: (count: number) => trackCustom("trade.kisSynced", { count }),
};

// ─────────────────────────────────────────────────────────────
// COMMUNITY — 커뮤니티
// ─────────────────────────────────────────────────────────────
export const COMMUNITY = {
  postCreated: (postId: string) =>
    trackCustom("community.postCreated", { postId }),
  commentCreated: (postId: string, commentId: string) =>
    trackCustom("community.commentCreated", { postId, commentId }),
  postLiked: (postId: string) => trackCustom("community.postLiked", { postId }),
  postBookmarked: (postId: string) =>
    trackCustom("community.postBookmarked", { postId }),
  leaderboardShown: () => trackCustom("community.leaderboardShown", {}),
  leaderboardClicked: (rank: number) =>
    trackCustom("community.leaderboardClicked", { rank }),
};

// ─────────────────────────────────────────────────────────────
// PAYMENT — 결제
// ─────────────────────────────────────────────────────────────
export const PAYMENT = {
  trialStarted: (plan: string) => trackCustom("payment.trialStarted", { plan }),
  paid: (plan: string, amount: number) =>
    trackCustom("payment.paid", { plan, amount }),
  downgraded: (fromPlan: string, toPlan: string) =>
    trackCustom("payment.downgraded", { fromPlan, toPlan }),
  cancelled: (plan: string) => trackCustom("payment.cancelled", { plan }),
};

// ─────────────────────────────────────────────────────────────
// SUBSCRIBE — 가격 페이지
// ─────────────────────────────────────────────────────────────
export const SUBSCRIBE = {
  tierViewed: (tier: string) => trackCustom("subscribe.tierViewed", { tier }),
  planClicked: (params: { plan: string; period: "monthly" | "yearly" }) =>
    trackCustom("subscribe.planClicked", params),
};

// ─────────────────────────────────────────────────────────────
// RECOMMEND — 추천 (V1 정확도 측정 baseline)
// ─────────────────────────────────────────────────────────────
export const RECOMMEND = {
  shown: (params: {
    surface: RecommendSurface;
    ticker: string;
    rank: number;
    algorithm: RecommendAlgorithm;
  }) => trackCustom("recommend.shown", params),

  clicked: (params: {
    surface: RecommendSurface;
    ticker: string;
    rank: number;
    algorithm: RecommendAlgorithm;
  }) => trackCustom("recommend.clicked", params),

  dismissed: (params: {
    surface: RecommendSurface;
    ticker: string;
    rank: number;
    algorithm: RecommendAlgorithm;
  }) => trackCustom("recommend.dismissed", params),
};
